import React from 'react'
import PropTypes from 'prop-types'

import useModalVisibilityControls from '@hooks/useModalVisibilityControls'

import { CueCard, CueCardItem } from '@components/CueCard'
import { CardContent, CardHeading, CardFooter } from '@components/layout/Card'
import { InputFieldWithLabel } from '@components/Form/Fields'

import { Button } from '@components/Button'
import { CardActionButton } from '@components/layout/Card/CardActionButton'
import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '@components/Modal'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'


export const JointClientDetailsCard = ({ participantsQueries, forecast }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  const clientDetailsPerParticipant = participantsQueries.map((participantQuery) => {
    return {
      id: participantQuery?.participant.id,
      preferredName: participantQuery?.participant.preferredName,
      retirementAge: participantQuery?.scenario.data.baseScenario.retirementAge,
      expectedAge: participantQuery?.scenario.data.baseScenario.expectedAge,
      maxExpectedAge: participantQuery?.scenario.data.baseScenario.assumptions.clientMaxExpectedAge,
      currentAge: participantQuery?.participant.currentAge,
    }
  })

  return (
    <CueCard hoverBorder={false}>
      <CardHeading>
        <h5>Client details</h5>
      </CardHeading>
      <CardContent>
        {clientDetailsPerParticipant.map((participant) => {
          return (
            <React.Fragment key={`${participant.id}-fields`}>
              <CueCardItem
                key={`${participant.id}-retirement-age`}
                name={`${participant.preferredName}'s retirement age`}
                value={participant.retirementAge}
              />
              <CueCardItem
                key={`${participant.id}-expected-age`}
                name={`${participant.preferredName}'s life expectancy`}
                value={participant.expectedAge}
              />
            </React.Fragment>
          )
        })
        }
        {<JointClientAgeModal
          open={modalVisible}
          onClose={closeModal}
          participants={clientDetailsPerParticipant}
          participantsQueries={participantsQueries}
          forecast={forecast}
        />}
      </CardContent>
      <CardFooter>
        <CardActionButton icon="pencil" label="Update client details" onClick={openModal} />
      </CardFooter>
    </CueCard>
  )
}

JointClientDetailsCard.propTypes = {
  participantsQueries: PropTypes.arrayOf(PropTypes.shape({
    scenario: PropTypes.object.isRequired,
    participant: PropTypes.object.isRequired
  })).isRequired
}

const JointClientAgeModal = ({
  className,
  open,
  onClose,
  participants,
  participantsQueries,
  forecast
}) => {
  const [submitting, setSubmitting] = React.useState(false)

  const handleSubmit = async (values) => {
    setSubmitting(true)
    participantsQueries.map(async (participantQuery) => {
      await participantQuery.scenario.actions.updateWithForecastInvalidation({
        retirement_age: values[`${participantQuery.participant.id}-retirement-age`],
        expected_age: values[`${participantQuery.participant.id}-expected-age`],
      })
      await forecast.refetch()
    })
  }

  const validationSchema = Yup.object().shape(
    participants.reduce((acc, participant) => {
      return {
        ...acc,
        [`${participant.id}-retirement-age`]: Yup.number()
          .min(participant.currentAge, `Retirement age cannot be in the past (current age: ${participant.currentAge}).`)
          .max(participant.expectedAge - 1, `Must be less than the life expectancy (${participant.expectedAge}).`)
          .required("Retirement age is required."),
        [`${participant.id}-expected-age`]: Yup.number()
          .min(participant.currentAge + 1, `Must be more than current age (${participant.currentAge}).`)
          .max(participant.maxExpectedAge, `Can't be more than the max life expectancy (${participant.maxExpectedAge}).`)
          .required("Life expectancy is required."),
      }
    }, {})
  )

  return (
    <Modal open={open} onClose={onClose} className={className}>
      <ModalHeader>Client Details</ModalHeader>

      <ModalSubHeader>
        Change your client's important ages here
      </ModalSubHeader>

      <ModalContent>
        <Formik
          initialValues={participants.reduce((acc, participant) => {
            return {
              ...acc,
              [`${participant.id}-retirement-age`]: participant.retirementAge,
              [`${participant.id}-expected-age`]: participant.expectedAge,
            }
          }, {})}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSubmit(values);
            setSubmitting(false);
            onClose();
          }}
        >
          <FormikForm className="app-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: 40, width: "100%" }}>
              {participants.map((participant) => (
                <div key={`${participant.id}-age-details`}>
                  <InputFieldWithLabel
                    label={`${participant.preferredName}'s retirement age`}
                    name={`${participant.id}-retirement-age`}
                  />
                  <InputFieldWithLabel
                    label={`${participant.preferredName}'s life expectancy`}
                    name={`${participant.id}-expected-age`}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <Button secondary onClick={onClose}>Cancel</Button>
              <Button disabled={submitting} primary trackAs="Update joint client's retirement and life expectancy ages" type="submit">Save</Button>
            </div>
          </FormikForm>
        </Formik>
      </ModalContent>

    </Modal>
  )
}
